import React, { useState, useRef } from 'react';
import { AWS_CONFIG, validateFile } from '../config/aws';
import { putObject } from '../utils/uploadToStorage';
import { optimizeAsset } from '../services/assetOptimizer';
import { analyzeGLB, optimizeGLB } from '../services/clientAssetProcessor';
import { Upload, X, Loader } from 'lucide-react';
import { supabase } from '../config/supabase';
import { AUTO_THUMB_MAX_BYTES, makeThumbnailFor, thumbKindFor } from '../utils/assetThumbnail';
import './S3Upload.css';

interface S3UploadProps {
  show: boolean;
  uploadType: 'environment' | 'skybox' | 'content' | 'texture';
  onSuccess: (result: any) => void;
  onClose: () => void;
  onError?: (error: any) => void;
}

const S3UploadComponent: React.FC<S3UploadProps> = ({
  show,
  uploadType,
  onSuccess,
  onClose,
  onError
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<import('../services/clientAssetProcessor').AssetAnalysis | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<AbortController | null>(null);

  if (!show) return null;

  const settings = AWS_CONFIG.uploadSettings[uploadType];
  
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file, uploadType);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setAnalysis(null);

    // Analyze GLB files
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'glb' || ext === 'gltf') {
      setAnalyzing(true);
      try {
        const buffer = await file.arrayBuffer();
        const result = await analyzeGLB(buffer);
        setAnalysis(result);
      } catch (err) {
        console.warn('[S3Upload] GLB analysis failed:', err);
      }
      setAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const extension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
      const timestamp = Date.now();
      const sanitized = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '-');
      const fileName = `${timestamp}-${sanitized}`;
      const key = `${settings.folder}${fileName}`;
      
      const contentType = (settings.contentTypes as any)[extension] || 'application/octet-stream';
      
      console.log('[S3Upload] Starting upload:', {
        file: selectedFile.name,
        size: selectedFile.size,
        key: key,
        contentType: contentType
      });

      // Client-side GLB optimization before upload
      let uploadBody: File | Uint8Array = selectedFile;
      if (extension === 'glb' || extension === 'gltf') {
        try {
          setUploadProgress(0);
          const rawBuffer = await selectedFile.arrayBuffer();
          const analysis = await analyzeGLB(rawBuffer);
          console.log('[S3Upload] GLB analysis:', analysis);

          // Optimization disabled — always upload raw GLB
          // if (analysis.overBudget) { ... }
        } catch (err) {
          console.warn('[S3Upload] Client optimization failed, uploading raw:', err);
        }
      }

      // Presigned single PUT straight to R2 (server checks prefix/type/size)
      const controller = new AbortController();
      uploadRef.current = controller;
      const fileUrl = await putObject(key, uploadBody, contentType, {
        signal: controller.signal,
        onProgress: (percent) => setUploadProgress(percent),
      });
      console.log('[S3Upload] Upload complete:', key);

      // Save to database if user is authenticated (non-blocking)
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        try {
          const { data: inserted, error } = await supabase
            .from('user_uploads')
            .insert({
              user_id: user.id,
              type: uploadType,
              name: selectedFile.name,
              url: fileUrl,
              file_size: selectedFile.size,
              mime_type: contentType,
              metadata: {
                key: key,
                extension: extension,
                uploadedAt: Date.now()
              }
            })
            .select('id')
            .single();
          
          if (error) {
            console.error('[S3Upload] Failed to save to database:', error);
            console.error('[S3Upload] Database error details:', {
              code: error.code,
              message: error.message,
              details: error.details
            });
          } else {
            console.log('[S3Upload] Successfully saved to database');
            // Thumbnail from the local file — no re-download (non-blocking)
            const thumbFile = selectedFile;
            const kind = thumbKindFor(thumbFile.name, contentType);
            if (inserted?.id && kind !== 'none' && !(kind === 'model' && thumbFile.size > AUTO_THUMB_MAX_BYTES)) {
              makeThumbnailFor('user_uploads', inserted.id, thumbFile, thumbFile.name, contentType)
                .catch((e) => console.warn('[S3Upload] Thumbnail failed:', e));
            }
          }
        } catch (dbError) {
          console.error('[S3Upload] Database error:', dbError);
        }
      }
      
      // Trigger async asset optimization for GLB files (non-blocking)
      if (extension === 'glb') {
        optimizeAsset(key).then((result) => {
          if (result) {
            console.log(`[S3Upload] Asset optimized: ${result.reductionPct}% smaller, ${result.lods.length} LODs generated`);
          }
        }).catch(() => { /* optimization is best-effort */ });
      }

      // Call success callback (this is what adds the skybox to the UI)
      onSuccess({
        url: fileUrl,
        fileName: selectedFile.name,
        filesUploaded: [{
          url: fileUrl,
          filename: selectedFile.name,
          size: selectedFile.size,
          type: extension,
          key: key,
          uploadedAt: Date.now()
        }]
      });
      
      // Reset state
      setSelectedFile(null);
      setUploadProgress(0);
      onClose();
      
    } catch (err) {
      console.error('[S3Upload] Upload error:', err);
      console.error('[S3Upload] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        fullError: err
      });
      
      let errorMessage = 'Upload failed';
      if (err instanceof Error) {
        if (err.message.includes('NetworkingError') || err.message.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('No \'Access-Control-Allow-Origin\'')) {
          errorMessage = 'CORS error. Your S3 bucket needs proper CORS configuration.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setUploading(false);
      uploadRef.current = null;
    }
  };

  const handleCancel = () => {
    if (uploadRef.current) {
      uploadRef.current.abort();
      setUploading(false);
      setUploadProgress(0);
    }
    onClose();
  };

  const getAcceptString = () => {
    return settings.allowedFormats.map(ext => `.${ext}`).join(',');
  };

  const getTitle = () => {
    switch (uploadType) {
      case 'environment':
        return 'Upload Environment Model';
      case 'skybox':
        return 'Upload Skybox Image';
      case 'content':
        return 'Upload Content';
      case 'texture':
        return 'Upload Texture (Image or PBR Zip)';
      default:
        return 'Upload File';
    }
  };

  return (
    <div className="s3-upload-overlay">
      <div className="s3-upload-modal">
        <div className="s3-upload-header">
          <h2>{getTitle()}</h2>
          <button className="s3-upload-close" onClick={handleCancel}>
            <X size={20} />
          </button>
        </div>
        
        <div className="s3-upload-body">
          {error && (
            <div className="s3-error">
              {error}
            </div>
          )}
          
          <div 
            className="s3-dropzone"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptString()}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              multiple={uploadType === 'content'}
            />
            
            {selectedFile ? (
              <div className="s3-file-info">
                <p className="s3-file-name">{selectedFile.name}</p>
                <p className="s3-file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
                {analyzing && (
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Analyzing model...</p>
                )}
                {analysis && (
                  <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.3)', fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{
                        display: 'inline-block',
                        width: 10, height: 10, borderRadius: '50%',
                        background: analysis.triangles < 100_000 ? '#4ade80'
                          : analysis.triangles < 500_000 ? '#facc15'
                          : '#ef4444',
                        boxShadow: `0 0 6px ${analysis.triangles < 100_000 ? 'rgba(74,222,128,0.5)' : analysis.triangles < 500_000 ? 'rgba(250,204,21,0.5)' : 'rgba(239,68,68,0.5)'}`,
                      }} />
                      <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                        {analysis.triangles < 100_000 ? 'VR Ready' : analysis.triangles < 500_000 ? 'VR Caution' : 'VR Heavy'}
                      </span>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                      {Math.round(analysis.triangles / 1000)}K triangles &bull; {analysis.meshCount} meshes &bull; {analysis.textureCount} textures
                      {analysis.maxTextureWidth > 0 && <> &bull; max {analysis.maxTextureWidth}x{analysis.maxTextureHeight}px</>}
                    </div>
                    {analysis.warnings.length > 0 && (
                      <div style={{ marginTop: 4, color: analysis.triangles >= 500_000 ? '#ef4444' : '#facc15', fontSize: 11 }}>
                        {analysis.warnings.map((w, i) => <div key={i}>{w}</div>)}
                        {analysis.overBudget && <div style={{ marginTop: 2, color: 'rgba(255,255,255,0.5)' }}>Model will be uploaded as-is</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <Upload size={48} />
                <p>Click to select files or drag and drop</p>
                <p className="s3-formats">
                  Accepted formats: {settings.allowedFormats.join(', ')}
                </p>
                <p className="s3-max-size">
                  Max size: {settings.maxFileSize / (1024 * 1024 * 1024)} GB
                </p>
              </>
            )}
          </div>
          
          {uploading && (
            <div className="s3-progress">
              <div className="s3-progress-bar">
                <div 
                  className="s3-progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p>{uploadProgress}% uploaded</p>
            </div>
          )}
          
          <div className="s3-actions">
            <button 
              className="s3-button s3-button-cancel"
              onClick={handleCancel}
              disabled={uploading}
            >
              Cancel
            </button>
            <button 
              className="s3-button s3-button-upload"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? (
                <>
                  <Loader className="s3-spinner" size={16} />
                  Uploading...
                </>
              ) : (
                'Upload to S3'
              )}
            </button>
          </div>
          
          <div className="s3-info">
            <p>Powered by AWS S3 • No file size limits</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default S3UploadComponent;